import { useState } from 'react'

import { authClient } from '@/features/auth/lib/auth-client'

import { redirectToAdminUsers } from '../lib/impersonation-navigate'

/**
 * Under Better Auth, starting impersonation swaps the current tab's session
 * cookie for the impersonated one (there is no separate tab like the BetterAuth
 * actor-token flow). The admin therefore needs a visible affordance to end it.
 * The session comes from Better Auth's shared `useSession` store, which
 * `BetterAuthGuard` (also mounted in `_app`) has already subscribed to — this
 * banner used to issue its OWN `fetch('/api/auth/get-session')`, a second
 * uncoordinated round trip to the auth Worker on every page load in the app,
 * measured at 350-780ms in production. When `session.impersonatedBy`
 * is set we render a fixed banner with a "Stop impersonating" button that hits
 * the Better Auth admin plugin's `stop-impersonating` endpoint (same-origin via
 * the `/api/auth/*` proxy rewrite), which restores the admin session cookie,
 * then returns the admin to `/admin/users`.
 *
 */
interface SessionShape {
  session?: { impersonatedBy?: string | null } | null
  user?: { email?: string | null } | null
}

export function ImpersonationBanner() {
  const [stopping, setStopping] = useState(false)
  const { data } = authClient.useSession() as { data: SessionShape | null }

  // A session read failure resolves to `null` here, which just hides the
  // banner — it must never throw into render.
  const impersonatedEmail = data?.session?.impersonatedBy ? (data.user?.email ?? 'this customer') : null

  if (!impersonatedEmail) return null

  const handleStop = async () => {
    setStopping(true)
    try {
      await fetch('/api/auth/admin/stop-impersonating', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // Better Auth's body parser rejects a Content-Type: application/json
        // request with an empty body (415/400); this endpoint takes no fields,
        // so send an explicit empty object.
        body: '{}',
      })
    } catch {
      // Even if the call fails, fall through to navigation — the admin can retry
      // from the users page; leaving them stuck in the customer view is worse.
    } finally {
      redirectToAdminUsers()
    }
  }

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed inset-x-0 top-0 z-[100] flex items-center justify-center gap-3 border-b border-warning-border bg-warning-tint px-4 py-2 text-sm text-warning"
    >
      <span>
        Impersonating <strong className="font-semibold">{impersonatedEmail}</strong>
      </span>
      <button
        type="button"
        onClick={handleStop}
        disabled={stopping}
        className="min-h-8 rounded-pill border border-warning-border bg-card px-3 text-sm font-medium text-warning transition-colors hover:bg-warning-tint disabled:opacity-60"
      >
        {stopping ? 'Stopping…' : 'Stop impersonating'}
      </button>
    </div>
  )
}
